import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { a11yStyles } from "../../styles/a11y";
import { arrowLeft, arrowRight } from "../../svg";
import { CarouselControls } from "../carousel/carousel-controls";
import { debounce } from "../../functions";

@customElement("umb-market-card-deck-header")
export class UmbMarketCardDeckHeader extends LitElement {
  @property({ type: String })
  header!: string;

  @property({ type: String })
  subheader?: string;

  @property({ type: Boolean })
  static = false;

  @property({ type: String })
  link?: string;

  @property({ type: String })
  context?: "footer" | undefined;

  #controls!: CarouselControls;

  @property({ type: Object })
  get controls() {
    return this.#controls;
  }

  set controls(value: CarouselControls) {
    if (!value) return;

    this.#controls = value;
    this.#controls.initialize();

    this.#debouncedResize();
    this.requestUpdate();
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.#debouncedResize);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.#debouncedResize);
  }

  #handleResize = () => {
    this.style.display =
      this.controls?.allInOne && !this.header ? "none" : "flex";
  };

  #debouncedResize = debounce(this.#handleResize);

  #getHeader() {
    if (!this.header) {
      return null;
    }

    return html`<h2 class=${this.static ? "sr-only" : ""}>${this.header}</h2>`;
  }

  #getSubHeader() {
    if (!this.subheader) {
      return null;
    }

    return html`<small class=${this.static ? "sr-only" : ""}
      >${this.subheader}</small
    >`;
  }

  render() {
    return html`<div class="title-wrapper">
        ${this.#getHeader()} ${this.#getSubHeader()}
      </div>

      ${when(
        this.controls?.itemCount > 0 &&
          !this.static &&
          this.controls?.allInOne === false,
        () =>
          html` <div class="navigation">
              <umb-market-button
                aria-label="Previous"
                data-action="prev"
                @click=${() => this.controls.slide("prev")}
              >
                ${arrowLeft}
              </umb-market-button>
              <umb-market-button
                aria-label="Next"
                data-action="next"
                @click=${() => this.controls.slide("next")}
              >
                ${arrowRight}
              </umb-market-button>
            </div>
            <slot></slot>`
      )} `;
  }

  static styles = [
    a11yStyles,
    css`
      :host {
        display: flex;
        align-items: center;
        min-height: var(--btn-round-size);
        margin-bottom: var(--header-margin-bottom, var(--s-spacer));
      }

      div {
        display: flex;
        align-items: center;
        margin-left: var(--button-align, auto);
      }

      .title-wrapper {
        position: relative;
        margin: 0;
        display: block;
      }

      h2 {
        text-align: left;
        font-weight: 400;
        font-size: 26px;
        line-height: normal;
        margin: 0;
        color: var(--header-color, var(--uui-color-text));
      }

      umb-market-tooltip {
        margin-left: var(--s-spacer);
      }

      umb-market-button {
        --button-padding: 0;
        --button-border-radius: 100px;
        --button-background: var(--dominant-color, transparent);
        height: var(--xl-spacer);
        width: var(--xl-spacer);
        display:flex;
        align-items: center;
      }

      umb-market-button[disabled="true"] {
        --button-cursor: not-allowed;
        opacity: 0.5;
      }

      .navigation {
        display: flex;
        gap: var(--xs-spacer);
      }

      button > svg *,
      a.btn > svg *,
      button:hover > svg *,
      a.btn:hover > svg * {
        --fill: var(--dominant-color);
      }

      svg {
        display: block;
      }

      small {
        font-weight: var(--font-weight, 300);
        font-size: var(--s-spacer);
      }
    `,
  ];
}
